import type React from 'react';
import { useState } from 'react';
import { useReducedMotion } from '../../hooks';
import styles from './keyboard.module.scss';
import { KEYBOARD_LAYOUT_ROWS, type KeyboardLayout, useKeyboardLayout } from './keyboard-layout-context';

/** What is known about a letter: `hit` — right place; `near` — in the word elsewhere; `miss` — not in the word. */
export type KeyState = 'hit' | 'near' | 'miss';

/**
 * How a letter key is drawn, and its `data-state`: a `KeyState`, or `removed` — ruled out by a hint; `candidate` —
 * one of the `candidates`; `dimmed` — not one of them.
 */
export type KeyDisplayState = KeyState | 'removed' | 'candidate' | 'dimmed';

/** A letter `A`–`Z`, `Enter` or `Backspace`. */
export type KeyboardKey = string;

export interface KeyboardProps {
  /** Best-known state of each letter, keyed by upper-case letter. Letters with no entry are unknown. */
  keyStates?: Readonly<Record<string, KeyState>>;
  /**
   * Upper-case letters that could be in the answer. When set, these letters are lit and every other letter is dimmed.
   * Dimmed keys still work.
   */
  candidates?: ReadonlySet<string>;
  /** Upper-case letters ruled out by a hint: drawn grey like a miss, and named "ruled out by hint". */
  removed?: ReadonlySet<string>;
  onKey: (key: KeyboardKey) => void;
  disabled?: boolean;
  /** Accessible name of the keyboard. Defaults to `Keyboard`. */
  ariaLabel?: string;
  /** Label of the Enter key. Defaults to `Enter`. */
  enterLabel?: string;
  /** Overrides the layout from the nearest `WordsHappenKeyboardLayoutProvider`. Defaults to `qwerty`. */
  layout?: KeyboardLayout;
  className?: string;
}

/** The QWERTY letter rows. */
export const KEYBOARD_ROWS: readonly string[] = KEYBOARD_LAYOUT_ROWS.qwerty;

/** Spoken after the letter, so a state never rests on colour alone. */
export const KEY_STATE_LABEL: Record<KeyDisplayState, string> = {
  hit: 'correct position',
  near: 'in the word, wrong position',
  miss: 'not in the word',
  removed: 'ruled out by hint',
  candidate: 'candidate',
  dimmed: 'not a candidate',
};

const STATE_CLASS: Record<KeyDisplayState, string | undefined> = {
  hit: styles.hit,
  near: styles.near,
  miss: styles.miss,
  removed: styles.removed,
  candidate: undefined,
  dimmed: styles.dimmed,
};

/**
 * A letter's display state, by precedence: hit › near › miss › removed › candidate › dimmed. A guess's mark outranks a
 * hint, and a hint outranks the candidate list. Undefined when nothing is known and there is no candidate list.
 */
export const keyDisplayState = (
  letter: string,
  { keyStates = {}, candidates, removed }: Pick<KeyboardProps, 'keyStates' | 'candidates' | 'removed'>,
): KeyDisplayState | undefined => {
  const known = keyStates[letter];
  if (known) return known;
  if (removed?.has(letter)) return 'removed';
  if (candidates) return candidates.has(letter) ? 'candidate' : 'dimmed';
  return undefined;
};

/** Two copies of the pulse, so changing from one to the other restarts it. 0 never pulses. */
const pulseClass = (pulses: number): string | undefined =>
  pulses > 0 ? (pulses % 2 === 1 ? styles.pulseOdd : styles.pulseEven) : undefined;

/**
 * Keeps focus where it is when a key is tapped or clicked, so the next physical Enter still reaches
 * whatever handles it rather than re-pressing this key. Keyboard users can still Tab to a key.
 */
const keepFocus = (event: React.MouseEvent) => event.preventDefault();

const BackspaceGlyph = () => (
  <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 5H9l-6 7 6 7h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1Z" strokeLinejoin="round" />
    <path d="m12 9 6 6m0-6-6 6" strokeLinecap="round" />
  </svg>
);

interface LetterKeyProps {
  letter: string;
  state: KeyDisplayState | undefined;
  /** Pulse once each time `state` changes after the first render. */
  pulse: boolean;
  disabled: boolean;
  onKey: (key: KeyboardKey) => void;
}

const LetterKey: React.FC<LetterKeyProps> = ({ letter, state, pulse, disabled, onKey }) => {
  const [shown, setShown] = useState(state);
  const [pulses, setPulses] = useState(0);
  // Adjusting state during render, rather than in an effect, so the pulse starts on the render that shows the change.
  if (shown !== state) {
    setShown(state);
    if (pulse) setPulses((n) => n + 1);
  }

  return (
    <button
      type="button"
      className={[styles.key, state && STATE_CLASS[state], pulse && pulseClass(pulses)].filter(Boolean).join(' ')}
      data-state={state}
      aria-label={state ? `${letter}, ${KEY_STATE_LABEL[state]}` : letter}
      disabled={disabled}
      onMouseDown={keepFocus}
      onClick={() => onKey(letter)}
    >
      <span aria-hidden="true">{letter}</span>
    </button>
  );
};

/**
 * An on-screen keyboard with Backspace and Enter, showing each letter's best-known state. The letters are in the
 * `layout` order, or the nearest `WordsHappenKeyboardLayoutProvider`'s, QWERTY by default. With `candidates` or
 * `removed`, it also lights candidate letters, dims the rest and greys out letters a hint ruled out, and a key pulses
 * once when its state changes (not under reduced motion).
 */
export const Keyboard: React.FC<KeyboardProps> = ({
  keyStates = {},
  candidates,
  removed,
  onKey,
  disabled = false,
  ariaLabel = 'Keyboard',
  enterLabel = 'Enter',
  layout,
  className,
}) => {
  const providedLayout = useKeyboardLayout();
  const rows = KEYBOARD_LAYOUT_ROWS[layout ?? providedLayout];
  const reducedMotion = useReducedMotion();
  // Only the candidate keyboard pulses, so a keyboard without candidates or removed letters (Honeycomb's) is unchanged.
  const pulse = !reducedMotion && (candidates !== undefined || removed !== undefined);

  const letterKey = (letter: string) => (
    <LetterKey
      key={letter}
      letter={letter}
      state={keyDisplayState(letter, { keyStates, candidates, removed })}
      pulse={pulse}
      disabled={disabled}
      onKey={onKey}
    />
  );

  return (
    <div role="group" aria-label={ariaLabel} className={[styles.keyboard, className].filter(Boolean).join(' ')}>
      {rows.map((row, i) => (
        <div key={row} className={styles.row}>
          {i === rows.length - 1 && (
            <button
              type="button"
              className={`${styles.key} ${styles.wide}`}
              aria-label="Backspace"
              disabled={disabled}
              onMouseDown={keepFocus}
              onClick={() => onKey('Backspace')}
            >
              <BackspaceGlyph />
            </button>
          )}
          {[...row].map(letterKey)}
          {i === rows.length - 1 && (
            <button
              type="button"
              className={`${styles.key} ${styles.wide}`}
              disabled={disabled}
              onMouseDown={keepFocus}
              onClick={() => onKey('Enter')}
            >
              {enterLabel}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};
